const COLUMN_POSITION_NAMES = {
  1: "first",
  2: "second",
  3: "third",
  4: "fourth",
};

export const columnPositionName = (columnNumber) =>
  COLUMN_POSITION_NAMES[columnNumber] || "";

export const elementPanelLabel = (element) => {
  if (!element) return "";
  if (element.element_type === "conditional_row") return "Conditional Row";
  return humanizeStr(element.element_type);
};

export const sectionPanelLabel = (section) => {
  if (!section) return "Section";
  if (section.content_toggle_item_id || section.element_id) return "Row";
  return "Section";
};

export const sectionLayoutLabel = (section) => sectionPanelLabel(section);

const panelObjectLabel = (objectType, object, variant) => {
  switch (objectType) {
    case "steps":
      return `Step ${object.position}`;
    case "sections":
      return sectionPanelLabel(object);
    case "section_columns":
      return `Column ${object.position || object.id}`;
    case "elements":
      return elementPanelLabel(object);
    case "content_toggle_items":
      return (
        object.content ||
        humanizeStr(variant.elements[object.element_id]?.element_type || "")
      );
    default:
      return "";
  }
};

const sectionColumnCrumb = (section, columnNumber) => ({
  object_type: "sections",
  object_id: section.id,
  label: `Col ${columnNumber}`,
  column: columnPositionName(columnNumber),
  columnNumber,
});

const pushUnique = (crumbs, crumb) => {
  const exists = crumbs.some(
    (existing) =>
      existing.object_type === crumb.object_type &&
      existing.object_id === crumb.object_id &&
      existing.column === crumb.column
  );

  if (!exists) {
    crumbs.push(crumb);
  }
};

const pushStepAndSection = (crumbs, variant, section) => {
  const step = variant.steps[section.step_id];
  if (step) {
    pushUnique(crumbs, {
      object_type: "steps",
      object_id: step.id,
      label: `Step ${step.position}`,
    });
  }

  pushUnique(crumbs, {
    object_type: "sections",
    object_id: section.id,
    label: "Section",
  });
};

const appendElementContext = (crumbs, variant, element) => {
  const parentSection = variant.sections[element.section_id];
  if (!parentSection) return;

  pushStepAndSection(crumbs, variant, parentSection);

  if (element.column) {
    pushUnique(crumbs, sectionColumnCrumb(parentSection, element.column));
  }
};

const appendNestedSectionContext = (crumbs, variant, section) => {
  const rowElement = variant.elements[section.element_id];
  if (!rowElement) return;

  const parentSection = variant.sections[rowElement.section_id];
  if (!parentSection) return;

  const step = variant.steps[parentSection.step_id];
  if (step) {
    pushUnique(crumbs, {
      object_type: "steps",
      object_id: step.id,
      label: `Step ${step.position}`,
    });
  }

  pushUnique(crumbs, {
    object_type: "sections",
    object_id: parentSection.id,
    label: sectionPanelLabel(parentSection),
  });

  if (rowElement.column) {
    pushUnique(crumbs, sectionColumnCrumb(parentSection, rowElement.column));
  }

  if (section.content_toggle_item_id) {
    const toggleItem =
      variant.content_toggle_items[section.content_toggle_item_id];
    const collapseElement = toggleItem
      ? variant.elements[toggleItem.element_id]
      : null;

    if (collapseElement) {
      pushUnique(crumbs, {
        object_type: "elements",
        object_id: collapseElement.id,
        label: elementPanelLabel(collapseElement),
      });
    }
  }

  pushUnique(crumbs, {
    object_type: "sections",
    object_id: section.id,
    label: sectionPanelLabel(section),
  });
};

const appendTopLevelSectionContext = (crumbs, variant, section) => {
  const step = variant.steps[section.step_id];
  if (step) {
    pushUnique(crumbs, {
      object_type: "steps",
      object_id: step.id,
      label: `Step ${step.position}`,
    });
  }

  pushUnique(crumbs, {
    object_type: "sections",
    object_id: section.id,
    label: sectionPanelLabel(section),
  });
};

const appendSectionContext = (crumbs, variant, section) => {
  if (section.element_id) {
    appendNestedSectionContext(crumbs, variant, section);
    return;
  }

  if (section.content_toggle_item_id) {
    const toggleItem =
      variant.content_toggle_items[section.content_toggle_item_id];
    const parentElement = toggleItem
      ? variant.elements[toggleItem.element_id]
      : null;

    if (parentElement) {
      appendElementContext(crumbs, variant, parentElement);
      pushUnique(crumbs, {
        object_type: "elements",
        object_id: parentElement.id,
        label: elementPanelLabel(parentElement),
      });
    }
  }

  appendTopLevelSectionContext(crumbs, variant, section);
};

export const collectPanelBreadcrumbAncestors = (
  variant,
  getObjectParentLayer,
  objectType,
  objectId
) => {
  const crumbs = [];

  if (objectType === "section_columns") {
    const sectionColumn = variant.section_columns?.[objectId];
    const section = sectionColumn
      ? variant.sections[sectionColumn.section_id]
      : null;

    if (section) {
      appendSectionContext(crumbs, variant, section);
    }

    return crumbs;
  }

  if (objectType === "sections") {
    const section = variant.sections?.[objectId];
    if (!section) return crumbs;

    if (section.element_id) {
      const rowElement = variant.elements[section.element_id];
      if (rowElement) {
        appendElementContext(crumbs, variant, rowElement);

        if (section.content_toggle_item_id) {
          const toggleItem =
            variant.content_toggle_items[section.content_toggle_item_id];
          const collapseElement = toggleItem
            ? variant.elements[toggleItem.element_id]
            : null;

          if (collapseElement) {
            pushUnique(crumbs, {
              object_type: "elements",
              object_id: collapseElement.id,
              label: elementPanelLabel(collapseElement),
            });
          }
        }
      }
    } else if (section.content_toggle_item_id) {
      const toggleItem =
        variant.content_toggle_items[section.content_toggle_item_id];
      const parentElement = toggleItem
        ? variant.elements[toggleItem.element_id]
        : null;

      if (parentElement) {
        appendElementContext(crumbs, variant, parentElement);
        pushUnique(crumbs, {
          object_type: "elements",
          object_id: parentElement.id,
          label: elementPanelLabel(parentElement),
        });
      }
    } else {
      const step = variant.steps[section.step_id];
      if (step) {
        pushUnique(crumbs, {
          object_type: "steps",
          object_id: step.id,
          label: `Step ${step.position}`,
        });
      }
    }

    return crumbs;
  }

  let currentType = objectType;
  let currentId = objectId;

  while (true) {
    const parentLayer = getObjectParentLayer(currentType, currentId);
    const parent = parentLayer.object;
    const parentType = parentLayer.object_type;

    if (!parent) break;

    if (parentType === "sections") {
      appendSectionContext(crumbs, variant, parent);
      break;
    }

    if (parentType === "elements") {
      appendElementContext(crumbs, variant, parent);
      pushUnique(crumbs, {
        object_type: "elements",
        object_id: parent.id,
        label: panelObjectLabel("elements", parent, variant),
      });
    } else if (parentType === "content_toggle_items") {
      const parentElement = variant.elements[parent.element_id];
      if (parentElement) {
        appendElementContext(crumbs, variant, parentElement);
        pushUnique(crumbs, {
          object_type: "elements",
          object_id: parentElement.id,
          label: elementPanelLabel(parentElement),
        });
      }
    } else {
      pushUnique(crumbs, {
        object_type: parentType,
        object_id: parent.id,
        label: panelObjectLabel(parentType, parent, variant),
      });
    }

    currentType = parentType;
    currentId = parent.id;
  }

  return crumbs;
};

export const getPanelParentBackLink = (
  variant,
  getObjectParentLayer,
  objectType,
  objectId
) => {
  const parentLayer = getObjectParentLayer(objectType, objectId);
  const parent = parentLayer.object;
  const parentType = parentLayer.object_type;

  if (!parent) return undefined;

  if (objectType === "section_columns" && parentType === "sections") {
    return {
      type: "sections",
      id: parent.id,
      label: `${sectionLayoutLabel(parent)} Layout`,
    };
  }

  if (objectType !== "sections") return undefined;

  const section = variant.sections[objectId];
  if (!section) return undefined;

  if (parentType === "content_toggle_items") {
    const parentElement = variant.elements[parent.element_id];
    if (!parentElement) return undefined;

    if (parentElement.element_type === "conditional_row") {
      return {
        type: "elements",
        id: parentElement.id,
        label: "Edit Conditional Rows",
      };
    }

    return {
      type: "elements",
      id: parentElement.id,
      label: `${elementPanelLabel(parentElement)} Layout`,
    };
  }

  if (parentType === "elements") {
    if (parent.element_type === "conditional_row") {
      return {
        type: "elements",
        id: parent.id,
        label: "Edit Conditional Rows",
      };
    }

    const parentSection = variant.sections[parent.section_id];
    if (!parentSection) return undefined;

    return {
      type: "sections",
      id: parentSection.id,
      label: `${sectionLayoutLabel(parentSection)} Layout`,
    };
  }

  return undefined;
};
